import type { EnabledMfrrProductionUnit, MfrrBidResponse, MfrrMarket } from 'api/mfrr/mfrr.types'
import { formatDateTimeToLocalizedString } from 'helpers/dateTime.helper/dateTime.helper'
import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { colors } from 'styles/variables'
import {
  CMBID_DIRECTIONS,
  formatCmReadOnlyValue,
  getMfrrDirectionBackground,
  getMfrrDirectionInnerBorder,
  MFRR_TABLE_CELL_BORDER,
  MFRR_TABLE_GROUP_BORDER,
} from '../MfrrBidTable/MfrrBidTable.helper'
import {
  getMfrrDeliveredBidPeriodLabel,
  getMfrrDeliveredBidRows,
  getMfrrDeliveredBidUnits,
} from './MfrrLastDeliveredStatus.helper'

const COLUMNS_PER_UNIT = 3

type MfrrDeliveredBidsTableProps = {
  deliveredBids: MfrrBidResponse[]
  units: EnabledMfrrProductionUnit[]
  market: MfrrMarket
}

function getBorderRight(columnIndex: number, columnCount: number, innerBorder: string): string {
  if (columnIndex === columnCount - 1) {
    return 'none'
  }

  const isGroupEnd = columnIndex === 0 || (columnIndex - 1) % COLUMNS_PER_UNIT === COLUMNS_PER_UNIT - 1

  return isGroupEnd ? MFRR_TABLE_GROUP_BORDER : innerBorder
}

export function MfrrDeliveredBidsTable({ deliveredBids, units, market }: MfrrDeliveredBidsTableProps): ReactNode {
  const { t } = useTranslation()

  const bidUnits = getMfrrDeliveredBidUnits(deliveredBids, units)
  const rows = getMfrrDeliveredBidRows(deliveredBids)
  const columnCount = 1 + CMBID_DIRECTIONS.length * bidUnits.length * COLUMNS_PER_UNIT

  function getBidColumnIndex(directionIndex: number, unitIndex: number): number {
    return 1 + (directionIndex * bidUnits.length + unitIndex) * COLUMNS_PER_UNIT
  }

  return (
    <div className="mt-4 max-h-[32rem] overflow-auto rounded border border-gray-300">
      <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
        <colgroup>
          <col className="w-[7rem]" />
          {Array.from({ length: columnCount - 1 }, (_unused, index) => (
            <col key={`delivered-bids-col-${index}`} />
          ))}
        </colgroup>
        <thead className="sticky top-0 z-10 bg-gray-100">
          <tr>
            <th
              rowSpan={3}
              className="bg-gray-100 px-2 py-2 text-left text-xs font-semibold"
              style={{
                color: colors.black,
                borderRight: MFRR_TABLE_GROUP_BORDER,
                borderBottom: MFRR_TABLE_CELL_BORDER,
              }}
            >
              {t('Period')}
            </th>
            {CMBID_DIRECTIONS.map((direction, directionIndex) => (
              <th
                key={direction}
                colSpan={bidUnits.length * COLUMNS_PER_UNIT}
                className="px-2 py-2 text-center text-xs font-semibold"
                style={{
                  backgroundColor: getMfrrDirectionBackground(direction),
                  color: colors.black,
                  borderRight: getBorderRight(
                    (directionIndex + 1) * bidUnits.length * COLUMNS_PER_UNIT,
                    columnCount,
                    'none'
                  ),
                  borderBottom: getMfrrDirectionInnerBorder(direction),
                }}
              >
                {direction === 'up' ? t('Up') : t('Down')}
              </th>
            ))}
          </tr>
          <tr>
            {CMBID_DIRECTIONS.flatMap((direction, directionIndex) =>
              bidUnits.map((unit, unitIndex) => (
                <th
                  key={`${direction}-${unit.id}`}
                  colSpan={COLUMNS_PER_UNIT}
                  className="px-2 py-2 text-center text-xs font-semibold"
                  style={{
                    backgroundColor: getMfrrDirectionBackground(direction),
                    color: colors.black,
                    borderRight: getBorderRight(getBidColumnIndex(directionIndex, unitIndex) + 2, columnCount, 'none'),
                    borderBottom: getMfrrDirectionInnerBorder(direction),
                  }}
                >
                  {unit.name}
                </th>
              ))
            )}
          </tr>
          <tr>
            {CMBID_DIRECTIONS.flatMap((direction, directionIndex) =>
              bidUnits.flatMap((unit, unitIndex) => {
                const bidColumnIndex = getBidColumnIndex(directionIndex, unitIndex)
                const innerBorder = getMfrrDirectionInnerBorder(direction)
                const headerStyle = {
                  backgroundColor: getMfrrDirectionBackground(direction),
                  color: colors.black,
                  borderBottom: MFRR_TABLE_CELL_BORDER,
                }

                return [
                  <th
                    key={`${direction}-${unit.id}-volume`}
                    className="px-2 py-2 text-center text-xs font-semibold"
                    style={{ ...headerStyle, borderRight: getBorderRight(bidColumnIndex, columnCount, innerBorder) }}
                  >
                    {t('Volume')}
                  </th>,
                  <th
                    key={`${direction}-${unit.id}-price`}
                    className="px-2 py-2 text-center text-xs font-semibold"
                    style={{
                      ...headerStyle,
                      borderRight: getBorderRight(bidColumnIndex + 1, columnCount, innerBorder),
                    }}
                  >
                    {t('Price')}
                  </th>,
                  <th
                    key={`${direction}-${unit.id}-delivered`}
                    className="px-2 py-2 text-center text-xs font-semibold"
                    style={{
                      ...headerStyle,
                      borderRight: getBorderRight(bidColumnIndex + 2, columnCount, innerBorder),
                    }}
                  >
                    {t('Delivered at')}
                  </th>,
                ]
              })
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.startTime} className="bg-white">
              <td
                className="whitespace-nowrap px-2 py-2 text-left font-medium"
                style={{ borderRight: MFRR_TABLE_GROUP_BORDER, borderBottom: MFRR_TABLE_CELL_BORDER }}
              >
                {getMfrrDeliveredBidPeriodLabel(row.startTime, market)}
              </td>
              {CMBID_DIRECTIONS.flatMap((direction, directionIndex) =>
                bidUnits.flatMap((unit, unitIndex) => {
                  const bidColumnIndex = getBidColumnIndex(directionIndex, unitIndex)
                  const bid = row.bidsByUnitId.get(unit.id)?.[direction]

                  return [
                    <td
                      key={`${direction}-${unit.id}-volume`}
                      className="px-2 py-2 text-right"
                      style={{
                        borderRight: getBorderRight(bidColumnIndex, columnCount, MFRR_TABLE_CELL_BORDER),
                        borderBottom: MFRR_TABLE_CELL_BORDER,
                      }}
                    >
                      {bid ? formatCmReadOnlyValue(bid.volume) : '-'}
                    </td>,
                    <td
                      key={`${direction}-${unit.id}-price`}
                      className="px-2 py-2 text-right"
                      style={{
                        borderRight: getBorderRight(bidColumnIndex + 1, columnCount, MFRR_TABLE_CELL_BORDER),
                        borderBottom: MFRR_TABLE_CELL_BORDER,
                      }}
                    >
                      {bid ? formatCmReadOnlyValue(bid.price) : '-'}
                    </td>,
                    <td
                      key={`${direction}-${unit.id}-delivered`}
                      className="whitespace-nowrap px-2 py-2 text-right"
                      style={{
                        borderRight: getBorderRight(bidColumnIndex + 2, columnCount, MFRR_TABLE_CELL_BORDER),
                        borderBottom: MFRR_TABLE_CELL_BORDER,
                      }}
                    >
                      {bid?.delivered_at ? formatDateTimeToLocalizedString(bid.delivered_at, true) : '-'}
                    </td>,
                  ]
                })
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
